/**
 * Editor HTML → outgoing mail HTML (DESIGN.md T11). Runs in the browser right before sending.
 *
 * - Pictures are embedded: every data: image becomes an inline attachment with a generated
 *   Content-ID and the <img> points to `cid:…` (the platform API sends them as
 *   multipart/related parts). Identical pictures are sent once.
 * - Mail-client friendly layout: paragraphs without vertical margins (Enter = new line, like
 *   Outlook), empty paragraphs keep their height, quotes get a visible bar, and the body sets a
 *   font so clients without defaults (Outlook desktop: Times New Roman) look right.
 *
 * The server sanitizes the result again (allowlist) and generates the plain-text alternative.
 */

/** Default body font of sent mail (also the editor's "Normal" size). */
export const MAIL_FONT_STYLE = 'font-family: Calibri, Arial, Helvetica, sans-serif; font-size: 11pt';
const QUOTE_STYLE = 'margin: 0 0 0 0.8ex; border-left: 1px solid #cccccc; padding-left: 1ex';

export interface InlineImage {
  contentId: string;
  name: string;
  contentType: 'image/png' | 'image/jpeg' | 'image/gif' | 'image/webp';
  contentBase64: string;
}

export interface OutgoingHtml {
  html: string;
  inlineImages: InlineImage[];
}

const DATA_URL = /^data:(image\/(?:png|jpeg|gif|webp));base64,([A-Za-z0-9+/=\s]+)$/i;
const EXTENSIONS: Record<InlineImage['contentType'], string> = { 'image/png': 'png', 'image/jpeg': 'jpg', 'image/gif': 'gif', 'image/webp': 'webp' };

/** File name for an inline image: the alt text if it looks like a file name, else image-N.ext. */
function imageName(alt: string | null, index: number, type: InlineImage['contentType']): string {
  const name = (alt ?? '').trim();
  return /^[^\\/:*?"<>|]+\.[a-z0-9]{2,5}$/i.test(name) ? name : `image-${index}.${EXTENSIONS[type]}`;
}

function newContentId(index: number): string {
  return `img${index}.${crypto.randomUUID()}@platform`;
}

export interface OutgoingOptions {
  /**
   * Content-ID of a picture of the quoted original shown from the platform API (reply/forward),
   * undefined for any other source. Such pictures go out as `cid:` references to the original's
   * parts, which the server attaches.
   */
  quotedImageContentId?: (src: string) => string | undefined;
}

/** Moves data: images into inline attachments and rewrites their src to cid: (see module comment). */
function embedImages(root: HTMLElement, options: OutgoingOptions): InlineImage[] {
  const byDataUrl = new Map<string, InlineImage>();
  for (const image of Array.from(root.querySelectorAll('img'))) {
    const src = image.getAttribute('src') ?? '';
    const quoted = options.quotedImageContentId?.(src);
    if (quoted !== undefined) {
      image.setAttribute('src', `cid:${quoted}`);
      continue;
    }
    const match = DATA_URL.exec(src);
    if (!match?.[1] || !match[2]) {
      // Only embedded pictures are sent; remote ones only inside the quote (the user allowed them there).
      const remoteInQuote = /^https:\/\//i.test(src) && image.closest('[data-gx-quote]') !== null;
      if (!src.startsWith('cid:') && !remoteInQuote) image.remove();
      continue;
    }
    let inline = byDataUrl.get(src);
    if (!inline) {
      const contentType = match[1].toLowerCase() as InlineImage['contentType'];
      const index = byDataUrl.size + 1;
      inline = { contentId: newContentId(index), name: imageName(image.getAttribute('alt'), index, contentType), contentType, contentBase64: match[2].replace(/\s+/g, '') };
      byDataUrl.set(src, inline);
    }
    image.setAttribute('src', `cid:${inline.contentId}`);
  }
  return [...byDataUrl.values()];
}

function appendStyle(element: HTMLElement, style: string): void {
  const current = element.getAttribute('style')?.trim().replace(/;$/, '');
  element.setAttribute('style', current ? `${style}; ${current}` : style);
}

/** Editor HTML in the mail-client layout (paragraphs, quotes, body font) as a wrapper element. */
function mailLayout(editorHtml: string): HTMLElement {
  const doc = new DOMParser().parseFromString(`<div>${editorHtml}</div>`, 'text/html');
  const root = doc.body.firstElementChild as HTMLElement;
  // Drop empty paragraphs at the end (the editor keeps one after a final list or signature).
  while (root.lastElementChild?.tagName === 'P' && root.lastElementChild.childNodes.length === 0) root.lastElementChild.remove();
  for (const paragraph of Array.from(root.querySelectorAll('p'))) {
    appendStyle(paragraph, 'margin-top: 0; margin-bottom: 0');
    if (paragraph.childNodes.length === 0) paragraph.append(doc.createElement('br'));
  }
  for (const quote of Array.from(root.querySelectorAll('blockquote'))) appendStyle(quote, QUOTE_STYLE);
  root.setAttribute('style', MAIL_FONT_STYLE);
  return root;
}

/** Editor HTML as it will look in the recipient's client, pictures unchanged (previews, e.g. of a signature). */
export function mailLayoutHtml(editorHtml: string): string {
  return mailLayout(editorHtml).outerHTML;
}

export function prepareOutgoingHtml(editorHtml: string, options: OutgoingOptions = {}): OutgoingHtml {
  const root = mailLayout(editorHtml);
  const inlineImages = embedImages(root, options);
  return { html: root.outerHTML, inlineImages };
}
